import { useState, useEffect, useRef } from 'react';
import { Send, DollarSign, CreditCard, ArrowUpRight, ArrowDownLeft, Mail, Lock, User, Plus, Shield, Zap, Clock, X, CheckCircle, AlertCircle, Info, MessageCircle, Bot } from 'lucide-react';
import './i18n';
import { useTranslation } from 'react-i18next';

// API Configuration
const API_URL = process.env.REACT_APP_API_URL || 'https://postgresql-production-f5ce.up.railway.app/api';

// P.A.Y.S. Logo Component
const PaysLogo = ({ size = 'normal', showTagline = true }) => {
  const sizes = {
    small: { icon: 'w-6 h-6', text: 'text-xl', tagline: 'text-xs' },
    normal: { icon: 'w-12 h-12', text: 'text-5xl', tagline: 'text-xl' },
    large: { icon: 'w-16 h-16', text: 'text-6xl', tagline: 'text-2xl' }
  };
  
  const s = sizes[size];
  
  return (
    <div className="text-center">
      <div className="inline-block bg-blue-600 p-4 rounded-full mb-3">
        <DollarSign className={`${s.icon} text-white`} />
      </div>
      <h1 className={`${s.text} font-bold text-blue-600 mb-1`}>P.A.Y.S.</h1>
      {showTagline && (
        <p className={`${s.tagline} font-semibold text-gray-700`}>Pay All Your Stuff</p>
      )}
    </div>
  );
};

// Header Logo Component
const HeaderLogo = () => {
  return (
    <div className="flex items-center gap-2">
      <div className="bg-white p-2 rounded-full">
        <DollarSign className="w-6 h-6 text-blue-600" />
      </div>
      <div>
        <h1 className="text-xl font-bold text-white">P.A.Y.S.</h1>
        <p className="text-xs text-blue-200">Pay All Your Stuff</p>
      </div>
    </div>
  );
};

// 2FA Tutorial Modal
const TwoFATutorial = ({ isOpen, onClose, onEnable }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 rounded-t-lg">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <Shield className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Enable Two-Factor Authentication</h2>
                <p className="text-blue-100 text-sm">Add an extra layer of security to your account</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white hover:text-blue-200">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* What is 2FA */}
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              What is Two-Factor Authentication?
            </h3>
            <p className="text-gray-600">
              Two-Factor Authentication (2FA) adds an extra layer of security to your account. 
              When you log in, you'll need both your password AND a verification code sent to your email.
            </p>
          </div>

          {/* How it works */}
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              How It Works
            </h3>
            <div className="space-y-3">
              <div className="flex gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                  1
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Enter Your Email & Password</p>
                  <p className="text-sm text-gray-600">Log in to P.A.Y.S. as usual</p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                  2
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Receive 6-Digit Code</p>
                  <p className="text-sm text-gray-600">We'll send a verification code to your email</p>
                </div>
              </div>

              <div className="flex gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold flex-shrink-0">
                  3
                </div>
                <div>
                  <p className="font-semibold text-gray-800">Enter the Code</p>
                  <p className="text-sm text-gray-600">Type the 6-digit code to complete login</p>
                </div>
              </div>
            </div>
          </div>

          {/* Example */}
          <div className="bg-gray-50 p-4 rounded-lg border-2 border-gray-200">
            <h4 className="font-bold text-gray-800 mb-2">Example Email:</h4>
            <div className="bg-white p-4 rounded border border-gray-300">
              <p className="text-sm text-gray-600 mb-2">Subject: <span className="font-semibold">P.A.Y.S. - Your Verification Code</span></p>
              <div className="border-t pt-3">
                <p className="text-gray-700 mb-3">Your verification code is:</p>
                <p className="text-3xl font-bold text-blue-600 text-center py-2 bg-blue-50 rounded tracking-wider">
                  123456
                </p>
                <p className="text-xs text-gray-500 mt-3">This code will expire in 10 minutes.</p>
              </div>
            </div>
          </div>

          {/* Why enable */}
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
            <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-600" />
              Why Enable 2FA?
            </h4>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>✓ Protects your money even if someone knows your password</li>
              <li>✓ Prevents unauthorized access to your account</li>
              <li>✓ Industry standard security practice</li>
              <li>✓ Takes only 30 seconds to set up</li>
            </ul>
          </div>

          {/* Important notes */}
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <h4 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Important Notes
            </h4>
            <ul className="space-y-1 text-sm text-gray-700">
              <li>• Make sure you have access to your email</li>
              <li>• Codes expire after 10 minutes</li>
              <li>• Check your spam folder if you don't see the email</li>
              <li>• You can disable 2FA anytime in settings</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-100 p-6 rounded-b-lg flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-semibold"
          >
            Maybe Later
          </button>
          <button
            onClick={() => {
              onClose();
              onEnable();
            }}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold flex items-center gap-2"
          >
            <Shield className="w-5 h-5" />
            Enable 2FA Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default function PaysApp() {
  const { t, i18n } = useTranslation();
  
  const [screen, setScreen] = useState('landing');
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  
  // Auth states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  
  // 2FA states
  // eslint-disable-next-line no-unused-vars
  const [_show2FASetup, setShow2FASetup] = useState(false);
  const [show2FATutorial, setShow2FATutorial] = useState(false);
  const [twoFactorEmail, setTwoFactorEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [_awaiting2FA, setAwaiting2FA] = useState(false);
  const [tempUserId, setTempUserId] = useState(null);
  
  // Stripe states
  // eslint-disable-next-line no-unused-vars
  const [_stripeConnected, setStripeConnected] = useState(false);
  const [stripeVerified, setStripeVerified] = useState(false);
  
  // App states
  const [balance, setBalance] = useState(0);
  const [activeTab, setActiveTab] = useState('home');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Transaction states
  const [sendEmail, setSendEmail] = useState('');
  const [sendAmount, setSendAmount] = useState('');
  const [sendNote, setSendNote] = useState('');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawalSpeed, setWithdrawalSpeed] = useState('standard'); // NEW: standard or instant
  
  // International Withdrawal states
  const [withdrawType, setWithdrawType] = useState('usd'); // 'usd' or 'international'
  const [withdrawCurrency, setWithdrawCurrency] = useState('EUR');
  const [withdrawQuote, setWithdrawQuote] = useState(null);
  const [withdrawQuoteLoading, setWithdrawQuoteLoading] = useState(false);
  const [withdrawRecipientName, setWithdrawRecipientName] = useState('');
  const [withdrawAccountNumber, setWithdrawAccountNumber] = useState('');
  const [withdrawBankCode, setWithdrawBankCode] = useState('');
  
  // Add Funds states
  const [addAmount, setAddAmount] = useState('');
  
  // Admin revenue states
  const [isAdmin, setIsAdmin] = useState(false);
  const [revenueData, setRevenueData] = useState(null);
  const [adminWithdrawAmount, setAdminWithdrawAmount] = useState('');
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // AI Support Chat states
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [supportMessages, setSupportMessages] = useState([]);
  const [supportInput, setSupportInput] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Password Recovery states
  const [forgotPasswordScreen, setForgotPasswordScreen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [resetStep, setResetStep] = useState(1); // 1: enter email, 2: enter code, 3: set new password
  
  // Deposit Fee estimate
  const [depositFeeEstimate, setDepositFeeEstimate] = useState(null);

  // International Transfer states
  const [intlAmount, setIntlAmount] = useState('');
  const [intlCurrency, setIntlCurrency] = useState('EUR');
  const [intlCountry, setIntlCountry] = useState('');
  const [intlRecipientName, setIntlRecipientName] = useState('');
  const [intlRecipientEmail, setIntlRecipientEmail] = useState('');
  const [intlAccountNumber, setIntlAccountNumber] = useState('');
  const [intlBankCode, setIntlBankCode] = useState('');
  const [intlNote, setIntlNote] = useState('');
  const [intlQuote, setIntlQuote] = useState(null);
  const [intlQuoteLoading, setIntlQuoteLoading] = useState(false);

  // API Helper
  const apiCall = async (endpoint, method = 'GET', body = null) => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const config = {
      method,
      headers,
      ...(body && { body: JSON.stringify(body) })
    };

    const response = await fetch(`${API_URL}${endpoint}`, config);
    const data = await response.json();
    
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  // Load user profile
  useEffect(() => {
    if (token) {
      loadUserProfile();
      checkStripeStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadUserProfile = async () => {
    try {
      const data = await apiCall('/user/profile');
      setUser(data.user);
      setBalance(data.user.balance);
      setStripeConnected(data.user.stripeConnected);
      setStripeVerified(data.user.stripeVerified);
      
      // Check if user is admin
      if (data.user.email === 'sniper44x@gmail.com') {
        setIsAdmin(true);
        loadRevenueData();
      }
      
      setScreen('dashboard');
    } catch (error) {
      console.error('Profile load error:', error);
      localStorage.removeItem('token');
      setToken(null);
      setScreen('landing');
    }
  };

  const checkStripeStatus = async () => {
    try {
      const data = await apiCall('/stripe/status');
      setStripeConnected(data.connected);
      setStripeVerified(data.verified);
    } catch (error) {
      console.error('Stripe status error:', error);
    }
  };

  // AI Support Chat Functions
  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    if (!supportInput.trim() || supportLoading) return;

    const userMessage = supportInput.trim();
    setSupportInput('');

    // Add user message to chat
    const newMessages = [...supportMessages, { role: 'user', content: userMessage }];
    setSupportMessages(newMessages);
    setSupportLoading(true);

    try {
      // Build conversation history for context
      const conversationHistory = supportMessages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));

      const response = await fetch(`${API_URL}/support/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          conversationHistory
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get response');
      }

      // Add AI response to chat
      setSupportMessages([...newMessages, { role: 'assistant', content: data.response }]);
    } catch (error) {
      console.error('Support chat error:', error);
      // Add fallback message
      setSupportMessages([
        ...newMessages,
        { 
          role: 'assistant', 
          content: 'Sorry, I\'m having trouble responding right now. Please email support at sniper44x@gmail.com for assistance.' 
        }
      ]);
    } finally {
      setSupportLoading(false);
    }
  };

  const startNewSupportChat = () => {
    setSupportMessages([
      {
        role: 'assistant',
        content: 'Hi! 👋 I\'m the P.A.Y.S. support assistant. How can I help you today?'
      }
    ]);
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [supportMessages]);

  // Initialize support chat when opened
  useEffect(() => {
    if (showSupportChat && supportMessages.length === 0) {
      startNewSupportChat();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSupportChat]);

  // Calculate fees
  // Calculate deposit fees
  useEffect(() => {
    if (addAmount && parseFloat(addAmount) > 0) {
      const amount = parseFloat(addAmount);
      const stripeFee = (amount * 0.029) + 0.30;
      const total = amount + stripeFee;
      
      setDepositFeeEstimate({
        amount: amount,
        fee: stripeFee,
        total: total
      });
    } else {
      setDepositFeeEstimate(null);
    }
  }, [addAmount]);

  // Get international transfer quote when amount/currency changes
  useEffect(() => {
    if (intlAmount && parseFloat(intlAmount) > 0 && intlCurrency) {
      const timer = setTimeout(() => {
        getIntlQuote();
      }, 800); // Debounce to avoid too many API calls
      return () => clearTimeout(timer);
    } else {
      setIntlQuote(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intlAmount, intlCurrency]);

  // Get international withdrawal quote when amount/currency changes
  useEffect(() => {
    if (withdrawType === 'international' && withdrawAmount && parseFloat(withdrawAmount) > 0) {
      const timer = setTimeout(() => {
        getWithdrawalQuote();
      }, 800); // Debounce
      return () => clearTimeout(timer);
    } else {
      setWithdrawQuote(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [withdrawAmount, withdrawCurrency, withdrawType]);

  // Auth handlers
  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      alert('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const data = await apiCall('/auth/register', 'POST', { firstName, lastName, email, password });
      setToken(data.token);
      localStorage.setItem('token', data.token);
      setUser(data.user);
      setBalance(data.user.balance);
      setScreen('dashboard');
      alert('Account created successfully!');
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await apiCall('/auth/login', 'POST', { email, password });
      
      if (data.requiresTwoFactor) {
        setAwaiting2FA(true);
        setTempUserId(data.userId);
        setScreen('verify-2fa');
        alert('Verification code sent to your email!');
      } else {
        setToken(data.token);
        localStorage.setItem('token', data.token);
        setUser(data.user);
        setBalance(data.user.balance);
        setStripeConnected(data.user.stripeConnected);
        setStripeVerified(data.user.stripeVerified);
        setScreen('dashboard');
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await apiCall('/auth/verify-2fa', 'POST', {
        userId: tempUserId,
        code: verificationCode
      });
      
      setToken(data.token);
      localStorage.setItem('token', data.token);
      setUser(data.user);
      setBalance(data.user.balance);
      setStripeConnected(data.user.stripeConnected);
      setStripeVerified(data.user.stripeVerified);
      setScreen('dashboard');
      setVerificationCode('');
      setAwaiting2FA(false);
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnable2FA = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);
    try {
      await apiCall('/auth/enable-2fa', 'POST', { twoFactorEmail: twoFactorEmail || email });
      alert('2FA enabled! You will now receive verification codes when logging in.');
      setShow2FASetup(false);
      setShow2FATutorial(false);
      setTwoFactorEmail('');
      if (user) {
        loadUserProfile();
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable 2FA? This will make your account less secure.')) return;
    
    setLoading(true);
    try {
      await apiCall('/auth/disable-2fa', 'POST');
      alert('2FA has been disabled');
      loadUserProfile();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Stripe handlers
  const handleConnectStripe = async () => {
    setLoading(true);
    try {
      const data = await apiCall('/stripe/connect', 'POST');
      window.location.href = data.url;
    } catch (error) {
      alert(error.message);
      setLoading(false);
    }
  };

  // Add Funds handler (Stripe Checkout - SECURE)
  const handleAddFunds = async (e) => {
    e.preventDefault();
    
    if (!addAmount || parseFloat(addAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setLoading(true);
    try {
      const data = await apiCall('/transactions/add-funds', 'POST', {
        amount: parseFloat(addAmount)
      });
      
      // Show fee breakdown to user
      const confirmMessage = `Amount to add: $${data.amount.toFixed(2)}\nProcessing fee (Stripe): $${data.fee.toFixed(2)}\nTotal charge: $${data.total.toFixed(2)}\n\nDo you want to continue?`;

      if (!window.confirm(confirmMessage)) {
        setLoading(false);
        return;
      }
      
      // Redirect to Stripe Checkout (secure payment page)
      window.location.href = data.sessionUrl;
    } catch (error) {
      alert(error.message);
      setLoading(false);
    }
  };

  // Password Recovery Functions
  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiCall('/auth/request-password-reset', 'POST', { email: resetEmail });
      alert('If that email exists, a reset code has been sent. Check your inbox!');
      setResetStep(2);
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyResetCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await apiCall('/auth/verify-reset-code', 'POST', {
        email: resetEmail,
        code: resetCode
      });
      alert('Code verified! Now set your new password.');
      setResetStep(3);
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmNewPassword) {
      alert('Passwords do not match!');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters!');
      return;
    }

    setLoading(true);

    try {
      await apiCall('/auth/reset-password', 'POST', {
        email: resetEmail,
        code: resetCode,
        newPassword: newPassword
      });
      alert('Password reset successful! You can now log in with your new password.');
      
      // Reset all states and go to login
      setForgotPasswordScreen(false);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmNewPassword('');
      setResetStep(1);
      setScreen('login');
    } catch (error) {
      alert('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Admin revenue handlers
  const loadRevenueData = async () => {
    try {
      const data = await apiCall('/admin/revenue');
      setRevenueData(data);
    } catch (error) {
      console.error('Failed to load revenue data:', error);
    }
  };
  
  const handleAdminWithdraw = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Withdraw $${adminWithdrawAmount} from revenue? Money will be sent to your bank account linked to Stripe in 1-2 business days.`)) return;
    
    setLoading(true);
    try {
      const data = await apiCall('/admin/withdraw-revenue', 'POST', {
        amount: parseFloat(adminWithdrawAmount)
      });
      
      alert(`✅ ${data.message}\n\nWithdrawn: $${data.withdrawn}\nNew Available: $${data.newAvailable.toFixed(2)}`);
      setAdminWithdrawAmount('');
      loadRevenueData();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Transaction handlers
  const handleSendMoney = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await apiCall('/transactions/send', 'POST', {
        email: sendEmail,
        amount: parseFloat(sendAmount),
        note: sendNote
      });
      
      setBalance(data.balance);
      setSendEmail('');
      setSendAmount('');
      setSendNote('');
      alert(`✅ Money sent successfully! FREE & Instant. ${data.recipientReceived ? 'Recipient received the money.' : ''}`);
      loadTransactions();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!stripeVerified) {
      alert('Please complete Stripe verification to withdraw funds');
      return;
    }

    setLoading(true);
    try {
      const data = await apiCall('/transactions/withdraw', 'POST', {
        amount: parseFloat(withdrawAmount),
        withdrawalSpeed: withdrawalSpeed
      });
      
      setBalance(data.balance);
      setWithdrawAmount('');
      const speedText = withdrawalSpeed === 'instant' ? '30 min - 24 hours' : '1-2 days';
      alert(`Withdrawal initiated! Money will arrive in your bank in ${speedText}.`);
      loadTransactions();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  // International Withdrawal handlers
  const getWithdrawalQuote = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) <= 0 || withdrawType !== 'international') {
      setWithdrawQuote(null);
      return;
    }

    setWithdrawQuoteLoading(true);
    try {
      const data = await apiCall('/transactions/withdrawal-quote', 'POST', {
        amount: parseFloat(withdrawAmount),
        targetCurrency: withdrawCurrency
      });
      setWithdrawQuote(data);
    } catch (error) {
      console.error('Withdrawal quote error:', error);
      setWithdrawQuote(null);
    } finally {
      setWithdrawQuoteLoading(false);
    }
  };

  const handleWithdrawInternational = async (e) => {
    e.preventDefault();
    
    if (!withdrawQuote) {
      alert('Please wait for exchange rate quote');
      return;
    }

    const confirmMsg = `Withdraw to ${withdrawCurrency}?\n\nYou withdraw: $${withdrawAmount}\nYou receive: ${withdrawQuote.targetAmount.toFixed(2)} ${withdrawCurrency}\nFee: $${withdrawQuote.totalFee.toFixed(2)}\nTotal: $${withdrawQuote.totalCost.toFixed(2)}`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setLoading(true);
    try {
      const data = await apiCall('/transactions/withdraw-international', 'POST', {
        amount: parseFloat(withdrawAmount),
        targetCurrency: withdrawCurrency,
        recipientName: withdrawRecipientName,
        accountNumber: withdrawAccountNumber,
        bankCode: withdrawBankCode
      });

      setBalance(data.balance);
      
      // Clear form
      setWithdrawAmount('');
      setWithdrawRecipientName('');
      setWithdrawAccountNumber('');
      setWithdrawBankCode('');
      setWithdrawQuote(null);

      alert(`✅ Withdrawal initiated!\n\nYou receive: ${data.targetAmount.toFixed(2)} ${data.targetCurrency}\nEstimated delivery: ${data.estimatedDelivery}`);
      loadTransactions();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  // International Transfer handlers
  const getIntlQuote = async () => {
    if (!intlAmount || parseFloat(intlAmount) <= 0 || !intlCurrency) {
      return;
    }

    setIntlQuoteLoading(true);
    try {
      const data = await apiCall('/wise/quote', 'POST', {
        amount: parseFloat(intlAmount),
        targetCurrency: intlCurrency,
        country: intlCountry
      });
      setIntlQuote(data);
    } catch (error) {
      console.error('Quote error:', error);
      setIntlQuote(null);
    } finally {
      setIntlQuoteLoading(false);
    }
  };

  const handleIntlTransfer = async (e) => {
    e.preventDefault();
    
    if (!intlQuote) {
      alert('Please get a quote first');
      return;
    }

    const confirmMsg = `Send international transfer?\n\nYou send: $${intlAmount}\nRecipient gets: ${intlQuote.targetAmount.toFixed(2)} ${intlCurrency}\nFee: $${intlQuote.fee.toFixed(2)}\nTotal: $${(parseFloat(intlAmount) + intlQuote.fee).toFixed(2)}\n\nYou save $${intlQuote.westernUnionEstimate.youSave.toFixed(2)} vs Western Union!`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    setLoading(true);
    try {
      const data = await apiCall('/wise/transfer', 'POST', {
        amount: parseFloat(intlAmount),
        targetCurrency: intlCurrency,
        recipientName: intlRecipientName,
        recipientEmail: intlRecipientEmail,
        recipientCountry: intlCountry,
        accountNumber: intlAccountNumber,
        bankCode: intlBankCode,
        note: intlNote
      });

      setBalance(data.balance);
      
      // Clear form
      setIntlAmount('');
      setIntlRecipientName('');
      setIntlRecipientEmail('');
      setIntlAccountNumber('');
      setIntlBankCode('');
      setIntlNote('');
      setIntlQuote(null);

      alert(`✅ Transfer sent!\n\nRecipient receives: ${data.recipientReceives.toFixed(2)} ${data.currency}\nEstimated delivery: ${data.estimatedDelivery}`);
      loadTransactions();
    } catch (error) {
      alert(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      const data = await apiCall('/transactions');
      setTransactions(data.transactions);
    } catch (error) {
      console.error('Load transactions error:', error);
    }
  };

  useEffect(() => {
    if (token && activeTab === 'activity') {
      loadTransactions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, token]);

  // Handle Stripe Checkout return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const stripeStatus = urlParams.get('stripe');

    // Handle Stripe Checkout success
    if (paymentStatus === 'success') {
      // Remove query params
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Reload user data
      loadUserProfile();
      loadTransactions();
      
      alert('✅ Funds added successfully!');
      setAddAmount('');
    }

    // Handle Stripe Checkout cancellation
    if (paymentStatus === 'cancelled') {
      window.history.replaceState({}, document.title, window.location.pathname);
      alert('Payment cancelled');
    }

    // Handle Stripe Connect return (from onboarding)
    if (stripeStatus === 'success' || stripeStatus === 'refresh') {
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Check if we're in a popup window opened by window.open
      if (window.opener && !window.opener.closed) {
        // Tell parent to refresh
        window.opener.location.reload();
        // Close this popup
        window.close();
      } else {
        // Not a popup, just reload this page
        loadUserProfile();
        checkStripeStatus();
        alert('✅ Stripe verification updated! Refreshing...');
        setTimeout(() => window.location.reload(), 1000);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setScreen('landing');
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Landing Page
  if (screen === 'landing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="max-w-4xl w-full">
          <PaysLogo size="large" />
          
          <div className="mt-8 text-center space-y-4">
            <p className="text-2xl text-gray-700">Send money instantly. Withdraw securely. Pay all your stuff.</p>
            
            <div className="grid md:grid-cols-3 gap-4 mt-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <Zap className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg">Instant Transfers</h3>
                <p className="text-gray-600 text-sm">2.9% + $0.30</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <Clock className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg">Standard Transfers</h3>
                <p className="text-gray-600 text-sm">100% FREE</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <Shield className="w-12 h-12 text-purple-600 mx-auto mb-3" />
                <h3 className="font-bold text-lg">Secure Withdrawals</h3>
                <p className="text-gray-600 text-sm">$3.00 flat fee</p>
              </div>
            </div>
          </div>

          <div className="flex gap-4 justify-center mt-8">
            <button
              onClick={() => setScreen('signup')}
              className="px-8 py-4 bg-blue-600 text-white rounded-lg font-bold text-lg hover:bg-blue-700 shadow-lg"
            >
              Get Started Free
            </button>
            <button
              onClick={() => setScreen('login')}
              className="px-8 py-4 bg-white text-blue-600 rounded-lg font-bold text-lg hover:bg-gray-100 shadow-lg"
            >
              Log In
            </button>
          </div>

          {/* Beta Disclaimer on Landing */}
          <div className="mt-8 bg-orange-100 border-2 border-orange-400 rounded-lg p-4 text-center">
            <p className="text-orange-900 font-bold mb-1">⚠️ BETA SOFTWARE - TESTING PHASE</p>
            <p className="text-sm text-orange-800">For educational and testing purposes only • Not FDIC insured • Use at your own risk</p>
          </div>

          <div className="mt-4 text-center text-sm text-gray-600">
            <p>Powered by Stripe • In Development • Beta Version</p>
          </div>
        </div>
      </div>
    );
  }

  // Sign Up Page
  if (screen === 'signup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <PaysLogo size="small" />
          
          <h2 className="text-2xl font-bold text-gray-800 mt-6 mb-6 text-center">Create Your Account</h2>
          
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
              <div className="relative">
                <User className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="John"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
              <div className="relative">
                <User className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            </div>

            {/* Beta Disclaimer */}
            <div className="p-3 bg-orange-50 border-2 border-orange-300 rounded-lg">
              <p className="text-xs text-orange-900 font-semibold mb-1">⚠️ Beta Software Notice</p>
              <p className="text-xs text-orange-800">
                By signing up, you acknowledge this is experimental software for testing purposes only. 
                Not FDIC insured. Not for commercial use. Use at your own risk. Max $100 recommended.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <button onClick={() => setScreen('login')} className="text-blue-600 font-semibold hover:underline">
              Log In
            </button>
          </p>

          <button
            onClick={() => setScreen('landing')}
            className="mt-4 w-full py-2 text-gray-600 hover:text-gray-800"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // Password Recovery Page
  if (forgotPasswordScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <PaysLogo size="small" />
          
          <h2 className="text-2xl font-bold text-gray-800 mt-6 mb-2 text-center">Reset Password</h2>
          <p className="text-sm text-gray-600 mb-6 text-center">
            {resetStep === 1 && "Enter your email to receive a reset code"}
            {resetStep === 2 && "Enter the 6-digit code sent to your email"}
            {resetStep === 3 && "Create your new password"}
          </p>
          
          {resetStep === 1 && (
            <form onSubmit={handleRequestPasswordReset} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
          )}

          {resetStep === 2 && (
            <form onSubmit={handleVerifyResetCode} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">6-Digit Code</label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-2xl font-bold tracking-widest"
                  placeholder="000000"
                  required
                  maxLength={6}
                />
                <p className="text-xs text-gray-500 mt-2">Code sent to: {resetEmail}</p>
              </div>

              <button
                type="submit"
                disabled={loading || resetCode.length !== 6}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>

              <button
                type="button"
                onClick={() => setResetStep(1)}
                className="w-full py-2 text-blue-600 hover:underline text-sm"
              >
                ← Use Different Email
              </button>
            </form>
          )}

          {resetStep === 3 && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    required
                    minLength={6}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          )}

          <button
            onClick={() => {
              setForgotPasswordScreen(false);
              setResetEmail('');
              setResetCode('');
              setNewPassword('');
              setConfirmNewPassword('');
              setResetStep(1);
              setScreen('login');
            }}
            className="mt-6 w-full py-2 text-gray-600 hover:text-gray-800"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    );
  }

  // Login Page
  if (screen === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <PaysLogo size="small" />
          
          <h2 className="text-2xl font-bold text-gray-800 mt-6 mb-6 text-center">Welcome Back</h2>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Logging In...' : 'Log In'}
            </button>
          </form>

          <button
            onClick={() => setForgotPasswordScreen(true)}
            className="mt-4 w-full py-2 text-blue-600 hover:underline text-sm font-semibold"
          >
            Forgot Password?
          </button>

          <p className="mt-6 text-center text-sm text-gray-600">
            Don't have an account?{' '}
            <button onClick={() => setScreen('signup')} className="text-blue-600 font-semibold hover:underline">
              Sign Up
            </button>
          </p>

          <button
            onClick={() => setScreen('landing')}
            className="mt-4 w-full py-2 text-gray-600 hover:text-gray-800"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  // 2FA Verification Page
  if (screen === 'verify-2fa') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
          <div className="text-center mb-6">
            <div className="inline-block bg-blue-100 p-4 rounded-full mb-4">
              <Shield className="w-12 h-12 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Enter Verification Code</h2>
            <p className="text-gray-600 mt-2">We sent a 6-digit code to your email</p>
          </div>
          
          <form onSubmit={handleVerify2FA} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">6-Digit Code</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-2xl font-bold tracking-widest"
                placeholder="000000"
                required
                maxLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 disabled:bg-gray-400"
            >
              {loading ? 'Verifying...' : 'Verify & Log In'}
            </button>
          </form>

          <p className="mt-4 text-center text-sm text-gray-600">
            Didn't receive the code? Check your spam folder or try logging in again.
          </p>
        </div>
      </div>
    );
  }

  // Dashboard (rest of the app)
  if (screen === 'dashboard') {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 shadow-lg">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <HeaderLogo />
            <div className="flex items-center gap-3">
              {/* Language Switcher */}
              <select
                value={i18n.language}
                onChange={(e) => i18n.changeLanguage(e.target.value)}
                className="px-3 py-2 bg-blue-700 text-white rounded-lg text-sm border border-blue-600 cursor-pointer hover:bg-blue-800"
              >
                <option value="en">🇺🇸 English</option>
                <option value="es">🇪🇸 Español</option>
                <option value="zh">🇨🇳 中文</option>
              </select>
              
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-blue-700 rounded-lg hover:bg-blue-800 font-semibold"
              >
                {t('app.logout')}
              </button>
            </div>
          </div>
        </div>

        {/* Stripe Verification Banner */}
        {!stripeVerified && (
          <div className="bg-yellow-50 border-b-2 border-yellow-200 p-4">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-yellow-600" />
                <div>
                  <p className="font-semibold text-yellow-900">Complete Verification to Receive Money</p>
                  <p className="text-sm text-yellow-700">Connect with Stripe to start receiving payments</p>
                </div>
              </div>
              <button
                onClick={handleConnectStripe}
                disabled={loading}
                className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 font-semibold"
              >
                {loading ? 'Connecting...' : 'Connect Stripe'}
              </button>
            </div>
          </div>
        )}

        <div className="max-w-7xl mx-auto p-4">
          {/* BETA DISCLAIMER BANNER */}
          <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white shadow-xl mb-6 border-4 border-orange-600">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-8 h-8 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-xl font-bold mb-2">⚠️ BETA SOFTWARE - EXPERIMENTAL USE ONLY</h3>
                <div className="space-y-2 text-sm">
                  <p className="font-semibold">This platform is in active development and testing phase.</p>
                  <ul className="space-y-1 pl-4">
                    <li>• <strong>NOT FOR COMMERCIAL USE</strong> - For testing and educational purposes only</li>
                    <li>• <strong>NOT FDIC INSURED</strong> - Funds are not protected by federal insurance</li>
                    <li>• <strong>NO MONEY TRANSMITTER LICENSE</strong> - Not a licensed financial institution</li>
                    <li>• <strong>USE AT YOUR OWN RISK</strong> - Limited to small test amounts only</li>
                    <li>• <strong>UPGRADING SOON</strong> - Full FDIC insurance and compliance coming via Stripe Treasury</li>
                  </ul>
                  <p className="mt-3 font-semibold text-orange-100">
                    Recommended maximum balance: $100. Do not store large amounts.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Balance Card */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white shadow-xl mb-6">
            <p className="text-blue-200 text-sm font-semibold">{t('dashboard.balance')}</p>
            <p className="text-5xl font-bold mt-2">{formatCurrency(balance)}</p>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setActiveTab('add-funds')}
                className="flex-1 bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" />
                {t('dashboard.addFunds')}
              </button>
              <button
                onClick={() => setActiveTab('send')}
                className="flex-1 bg-white text-blue-600 py-3 rounded-lg font-bold hover:bg-blue-50 flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                {t('dashboard.sendMoney')}
              </button>
              <button
                onClick={() => setActiveTab('withdraw')}
                className="flex-1 bg-blue-800 text-white py-3 rounded-lg font-bold hover:bg-blue-900 flex items-center justify-center gap-2"
              >
                <ArrowUpRight className="w-5 h-5" />
                {t('dashboard.withdraw')}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-md mb-6">
            <div className="flex border-b overflow-x-auto">
              <button
                onClick={() => setActiveTab('add-funds')}
                className={`flex-1 py-4 font-semibold whitespace-nowrap ${activeTab === 'add-funds' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
              >
                {t('tabs.addFunds')}
              </button>
              <button
                onClick={() => setActiveTab('send')}
                className={`flex-1 py-4 font-semibold whitespace-nowrap ${activeTab === 'send' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
              >
                {t('tabs.send')}
              </button>
              <button
                onClick={() => setActiveTab('international')}
                className={`flex-1 py-4 font-semibold whitespace-nowrap ${activeTab === 'international' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
              >
                🌍 {t('tabs.international')}
              </button>
              <button
                onClick={() => setActiveTab('withdraw')}
                className={`flex-1 py-4 font-semibold whitespace-nowrap ${activeTab === 'withdraw' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
              >
                {t('tabs.withdraw')}
              </button>
              <button
                onClick={() => setActiveTab('activity')}
                className={`flex-1 py-4 font-semibold whitespace-nowrap ${activeTab === 'activity' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
              >
                {t('tabs.activity')}
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`flex-1 py-4 font-semibold whitespace-nowrap ${activeTab === 'settings' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-600'}`}
              >
                {t('tabs.settings')}
              </button>
            </div>

            <div className="p-6">
              {/* Add Funds Tab */}
              {activeTab === 'add-funds' && (
                <form onSubmit={handleAddFunds} className="space-y-4">
                  <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg mb-4 border-2 border-blue-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-blue-600 p-2 rounded-full">
                        <DollarSign className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-blue-800 font-bold">💳 Add Money Securely</p>
                        <p className="text-xs text-blue-600">Powered by Stripe - PCI Compliant</p>
                      </div>
                    </div>
                    <ul className="text-xs text-gray-700 space-y-1 mt-3">
                      <li>✅ Debit & Credit Cards Accepted</li>
                      <li>✅ Bank Account (ACH) Supported</li>
                      <li>✅ Instant Availability</li>
                      <li>✅ Secure Encrypted Processing</li>
                    </ul>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Amount to Add
                    </label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                      <input
                        type="number"
                        value={addAmount}
                        onChange={(e) => setAddAmount(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold"
                        placeholder="100.00"
                        step="0.01"
                        min="1"
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Minimum: $1.00 • No maximum limit
                    </p>
                  </div>

                  {/* DEPOSIT FEE BREAKDOWN */}
                  {depositFeeEstimate && (
                    <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <Info className="w-4 h-4 text-yellow-600" />
                        Fee Breakdown
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Amount to add:</span>
                          <span className="font-semibold text-gray-800">${depositFeeEstimate.amount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Processing fee (2.9% + $0.30):</span>
                          <span className="font-semibold text-yellow-700">+${depositFeeEstimate.fee.toFixed(2)}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between">
                          <span className="font-bold text-gray-800">Total charge:</span>
                          <span className="font-bold text-gray-900 text-lg">${depositFeeEstimate.total.toFixed(2)}</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">
                        💡 This fee covers Stripe's payment processing costs. Your balance will increase by ${depositFeeEstimate.amount.toFixed(2)}.
                      </p>
                    </div>
                  )}

                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Shield className="w-4 h-4 text-green-600" />
                      <span className="font-semibold">Secure Payment</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      You'll be redirected to Stripe's secure payment page. Your card details are never stored on our servers.
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !addAmount || parseFloat(addAmount) <= 0}
                    className="w-full py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-bold text-lg hover:from-green-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Continue to Secure Payment
                      </span>
                    )}
                  </button>

                  <div className="text-center">
                    <p className="text-xs text-gray-500">
                      By continuing, you agree to Stripe's{' '}
                      <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        Terms of Service
                      </a>
                    </p>
                  </div>
                </form>
              )}

              {/* Send Money Tab */}
              {activeTab === 'send' && (
                <form onSubmit={handleSendMoney} className="space-y-4">
                  {/* FREE & Instant Banner */}
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <Zap className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-bold text-green-800">FREE & Instant Transfers! ⚡</p>
                        <p className="text-sm text-green-600">Send money to any P.A.Y.S. user instantly with zero fees</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Recipient Email</label>
                    <input
                      type="email"
                      value={sendEmail}
                      onChange={(e) => setSendEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="friend@example.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Amount</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                      <input
                        type="number"
                        value={sendAmount}
                        onChange={(e) => setSendAmount(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="100.00"
                        step="0.01"
                        min="0.01"
                        max={balance}
                        required
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Available balance: {formatCurrency(balance)}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Note (Optional)</label>
                    <input
                      type="text"
                      value={sendNote}
                      onChange={(e) => setSendNote(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="For dinner"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !sendAmount || !sendEmail || parseFloat(sendAmount) > balance}
                    className="w-full py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-bold text-lg hover:from-green-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg"
                  >
                    {loading ? 'Sending...' : '⚡ Send Money - FREE & Instant'}
                  </button>

                  <p className="text-xs text-center text-gray-500">
                    💚 No fees • Instant delivery • Money arrives immediately
                  </p>
                </form>
              )}

              {/* International Transfer Tab */}
              {activeTab === 'international' && (
                <form onSubmit={handleIntlTransfer} className="space-y-4">
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg mb-4 border-2 border-green-200">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-green-600 p-2 rounded-full">
                        <DollarSign className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-green-800 font-bold">🌍 Send Money Worldwide</p>
                        <p className="text-xs text-green-600">Powered by Wise - Cheaper than Western Union!</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Amount (USD)</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                        <input
                          type="number"
                          value={intlAmount}
                          onChange={(e) => setIntlAmount(e.target.value)}
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="100"
                          step="0.01"
                          min="1"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Currency</label>
                      <select
                        value={intlCurrency}
                        onChange={(e) => setIntlCurrency(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      >
                        <option value="EUR">🇪🇺 EUR - Euro</option>
                        <option value="GBP">🇬🇧 GBP - British Pound</option>
                        <option value="INR">🇮🇳 INR - Indian Rupee</option>
                        <option value="MXN">🇲🇽 MXN - Mexican Peso</option>
                        <option value="PHP">🇵🇭 PHP - Philippine Peso</option>
                        <option value="CAD">🇨🇦 CAD - Canadian Dollar</option>
                        <option value="AUD">🇦🇺 AUD - Australian Dollar</option>
                        <option value="JPY">🇯🇵 JPY - Japanese Yen</option>
                        <option value="CNY">🇨🇳 CNY - Chinese Yuan</option>
                        <option value="BRL">🇧🇷 BRL - Brazilian Real</option>
                      </select>
                    </div>
                  </div>

                  {/* Live Quote Display */}
                  {intlQuote && (
                    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 space-y-2">
                      <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-600" />
                        Live Exchange Rate
                      </p>
                      <div className="space-y-1 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">You send:</span>
                          <span className="font-semibold text-gray-800">${intlQuote.sourceAmount.toFixed(2)} USD</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Transfer fee:</span>
                          <span className="font-semibold text-green-700">${intlQuote.fee.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Exchange rate:</span>
                          <span className="font-semibold text-gray-800">1 USD = {intlQuote.rate.toFixed(4)} {intlCurrency}</span>
                        </div>
                        <div className="border-t pt-2 flex justify-between">
                          <span className="font-bold text-gray-800">Recipient gets:</span>
                          <span className="font-bold text-blue-600 text-lg">{intlQuote.targetAmount.toFixed(2)} {intlCurrency}</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Total cost:</span>
                          <span className="font-semibold text-gray-700">${(intlQuote.sourceAmount + intlQuote.fee).toFixed(2)}</span>
                        </div>
                      </div>
                      
                      {/* Savings Banner */}
                      <div className="bg-green-100 border border-green-300 rounded-lg p-3 mt-3">
                        <p className="text-xs text-green-800 font-bold">
                          💰 You save ${intlQuote.westernUnionEstimate.youSave.toFixed(2)} vs Western Union!
                        </p>
                        <p className="text-xs text-green-700 mt-1">
                          Western Union would charge ~${intlQuote.westernUnionEstimate.fee.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  )}

                  {intlQuoteLoading && (
                    <div className="text-center py-3">
                      <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      <p className="text-sm text-gray-600 mt-2">Getting best exchange rate...</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Recipient Name</label>
                    <input
                      type="text"
                      value={intlRecipientName}
                      onChange={(e) => setIntlRecipientName(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="John Smith"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Recipient Email (optional)</label>
                    <input
                      type="email"
                      value={intlRecipientEmail}
                      onChange={(e) => setIntlRecipientEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="recipient@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Country</label>
                    <input
                      type="text"
                      value={intlCountry}
                      onChange={(e) => setIntlCountry(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="United Kingdom"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Account Number</label>
                    <input
                      type="text"
                      value={intlAccountNumber}
                      onChange={(e) => setIntlAccountNumber(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Account number or IBAN"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Code / Routing Number</label>
                    <input
                      type="text"
                      value={intlBankCode}
                      onChange={(e) => setIntlBankCode(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="SWIFT/BIC or routing number"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Note (optional)</label>
                    <input
                      type="text"
                      value={intlNote}
                      onChange={(e) => setIntlNote(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Payment for..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !intlQuote}
                    className="w-full py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-bold text-lg hover:from-green-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg"
                  >
                    {loading ? 'Sending...' : `Send ${intlQuote ? intlQuote.targetAmount.toFixed(2) : ''} ${intlCurrency}`}
                  </button>

                  <p className="text-xs text-center text-gray-500">
                    Powered by Wise • Secure international transfers • Usually arrives in 1-2 business days
                  </p>
                </form>
              )}

              {/* Withdraw Tab */}
              {activeTab === 'withdraw' && (
                <div>
                  {!stripeVerified ? (
                    <div className="text-center py-12">
                      <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Verification Required</h3>
                      <p className="text-gray-600 mb-6">Complete Stripe verification to withdraw funds</p>
                      <button
                        onClick={handleConnectStripe}
                        disabled={loading}
                        className="px-8 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
                      >
                        {loading ? 'Connecting...' : 'Connect Stripe'}
                      </button>
                    </div>
                  ) : (
                    <div>
                      {/* Withdrawal Type Selector */}
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Withdrawal Type
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => setWithdrawType('usd')}
                            className={`p-4 border-2 rounded-lg font-semibold transition-all ${
                              withdrawType === 'usd'
                                ? 'border-blue-600 bg-blue-50 text-blue-600'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            💵 USD (US Bank)
                          </button>
                          <button
                            type="button"
                            onClick={() => setWithdrawType('international')}
                            className={`p-4 border-2 rounded-lg font-semibold transition-all ${
                              withdrawType === 'international'
                                ? 'border-green-600 bg-green-50 text-green-600'
                                : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                            }`}
                          >
                            🌍 International
                          </button>
                        </div>
                      </div>

                      {withdrawType === 'usd' ? (
                        /* USD Withdrawal Form */
                        <form onSubmit={handleWithdraw} className="space-y-4">
                          <div className="bg-green-50 p-4 rounded-lg mb-4">
                            <p className="text-sm text-green-800">✓ Your Stripe account is verified</p>
                            <p className="text-xs text-green-600 mt-1">You can withdraw funds to your bank</p>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Withdrawal Amount (USD)</label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                              <input
                                type="number"
                                value={withdrawAmount}
                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                placeholder="100.00"
                                step="0.01"
                                min="0.01"
                                max={balance}
                                required
                              />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Available balance: {formatCurrency(balance)}</p>
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Withdrawal Speed</label>
                            <div className="space-y-2">
                              <label className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-blue-50">
                                <input
                                  type="radio"
                                  value="standard"
                                  checked={withdrawalSpeed === 'standard'}
                                  onChange={(e) => setWithdrawalSpeed(e.target.value)}
                                  className="w-5 h-5 text-blue-600"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Clock className="w-5 h-5 text-blue-500" />
                                      <span className="font-semibold">Standard</span>
                                    </div>
                                    <span className="text-sm text-blue-600 font-semibold">0.25% + $2</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">Arrives in 1-2 business days</p>
                                </div>
                              </label>

                              <label className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-yellow-50">
                                <input
                                  type="radio"
                                  value="instant"
                                  checked={withdrawalSpeed === 'instant'}
                                  onChange={(e) => setWithdrawalSpeed(e.target.value)}
                                  className="w-5 h-5 text-yellow-600"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Zap className="w-5 h-5 text-yellow-500" />
                                      <span className="font-semibold">Instant</span>
                                    </div>
                                    <span className="text-sm text-yellow-600 font-semibold">1.5% + $2</span>
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">Arrives in 30 min - 24 hours</p>
                                </div>
                              </label>
                            </div>
                          </div>

                          <button
                            type="submit"
                            disabled={loading || !withdrawAmount || parseFloat(withdrawAmount) <= 0}
                            className="w-full py-4 bg-blue-600 text-white rounded-lg font-bold text-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-all shadow-lg"
                          >
                            {loading ? 'Processing...' : 'Withdraw to Bank'}
                          </button>
                        </form>
                      ) : (
                        /* International Withdrawal Form */
                        <form onSubmit={handleWithdrawInternational} className="space-y-4">
                          <div className="bg-gradient-to-r from-green-50 to-blue-50 p-4 rounded-lg mb-4 border-2 border-green-200">
                            <p className="text-sm text-green-800 font-bold">🌍 Withdraw to Local Currency</p>
                            <p className="text-xs text-green-600">Receive money in your local bank account</p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Amount (USD)</label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                                <input
                                  type="number"
                                  value={withdrawAmount}
                                  onChange={(e) => setWithdrawAmount(e.target.value)}
                                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                  placeholder="100"
                                  step="0.01"
                                  min="1"
                                  required
                                />
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">Receive Currency</label>
                              <select
                                value={withdrawCurrency}
                                onChange={(e) => setWithdrawCurrency(e.target.value)}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="EUR">🇪🇺 EUR - Euro</option>
                                <option value="GBP">🇬🇧 GBP - British Pound</option>
                                <option value="INR">🇮🇳 INR - Indian Rupee</option>
                                <option value="MXN">🇲🇽 MXN - Mexican Peso</option>
                                <option value="PHP">🇵🇭 PHP - Philippine Peso</option>
                                <option value="CAD">🇨🇦 CAD - Canadian Dollar</option>
                                <option value="AUD">🇦🇺 AUD - Australian Dollar</option>
                                <option value="JPY">🇯🇵 JPY - Japanese Yen</option>
                                <option value="CNY">🇨🇳 CNY - Chinese Yuan</option>
                                <option value="BRL">🇧🇷 BRL - Brazilian Real</option>
                              </select>
                            </div>
                          </div>

                          {/* Live Quote Display */}
                          {withdrawQuoteLoading && (
                            <div className="text-center py-3">
                              <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                              <p className="text-sm text-gray-600 mt-2">Getting exchange rate...</p>
                            </div>
                          )}

                          {withdrawQuote && (
                            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 space-y-2">
                              <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                <Info className="w-4 h-4 text-blue-600" />
                                Exchange Rate Preview
                              </p>
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600">You withdraw:</span>
                                  <span className="font-semibold text-gray-800">${withdrawQuote.sourceAmount.toFixed(2)} USD</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Fee:</span>
                                  <span className="font-semibold text-gray-700">${withdrawQuote.totalFee.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600">Exchange rate:</span>
                                  <span className="font-semibold text-gray-800">1 USD = {withdrawQuote.exchangeRate.toFixed(4)} {withdrawCurrency}</span>
                                </div>
                                <div className="border-t pt-2 flex justify-between">
                                  <span className="font-bold text-gray-800">You receive:</span>
                                  <span className="font-bold text-blue-600 text-lg">{withdrawQuote.targetAmount.toFixed(2)} {withdrawCurrency}</span>
                                </div>
                                <div className="flex justify-between text-xs">
                                  <span className="text-gray-500">Total cost:</span>
                                  <span className="font-semibold text-gray-700">${withdrawQuote.totalCost.toFixed(2)}</span>
                                </div>
                                <div className="text-xs text-gray-500 mt-2">
                                  Delivery: {withdrawQuote.estimatedDelivery}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Recipient Bank Details */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Account Holder Name</label>
                            <input
                              type="text"
                              value={withdrawRecipientName}
                              onChange={(e) => setWithdrawRecipientName(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="John Smith"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Account Number / IBAN</label>
                            <input
                              type="text"
                              value={withdrawAccountNumber}
                              onChange={(e) => setWithdrawAccountNumber(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="GB29NWBK60161331926819"
                              required
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">Bank Code (SWIFT/BIC/Routing)</label>
                            <input
                              type="text"
                              value={withdrawBankCode}
                              onChange={(e) => setWithdrawBankCode(e.target.value)}
                              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                              placeholder="NWBKGB2L"
                              required
                            />
                          </div>

                          <button
                            type="submit"
                            disabled={loading || !withdrawQuote}
                            className="w-full py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-lg font-bold text-lg hover:from-green-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg"
                          >
                            {loading ? 'Processing...' : withdrawQuote ? `Withdraw ${withdrawQuote.targetAmount.toFixed(2)} ${withdrawCurrency}` : 'Withdraw'}
                          </button>

                          <p className="text-xs text-center text-gray-500">
                            Powered by Wise • Secure international transfers • 1-2 business days delivery
                          </p>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-4">Recent Transactions</h3>
                  
                  {transactions.length === 0 ? (
                    <div className="text-center py-12">
                      <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No transactions yet</p>
                      <p className="text-sm text-gray-500 mt-2">Send or receive money to see your activity here</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {transactions.map((transaction) => (
                        <div key={transaction.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                          <div className={`p-3 rounded-full ${
                            transaction.type === 'transfer_out' ? 'bg-red-100' :
                            transaction.type === 'transfer_in' ? 'bg-green-100' :
                            transaction.type === 'withdrawal' ? 'bg-blue-100' :
                            'bg-purple-100'
                          }`}>
                            {transaction.type === 'transfer_out' && <ArrowUpRight className="w-5 h-5 text-red-600" />}
                            {transaction.type === 'transfer_in' && <ArrowDownLeft className="w-5 h-5 text-green-600" />}
                            {transaction.type === 'withdrawal' && <ArrowUpRight className="w-5 h-5 text-blue-600" />}
                            {transaction.type === 'deposit' && <Plus className="w-5 h-5 text-purple-600" />}
                          </div>
                          
                          <div className="flex-1">
                            <p className="font-semibold text-gray-800">{transaction.description}</p>
                            <p className="text-sm text-gray-500">
                              {transaction.recipient_email || transaction.sender_email || 'Bank account'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {new Date(transaction.created_at).toLocaleString()}
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <p className={`font-bold text-lg ${
                              transaction.type === 'transfer_out' || transaction.type === 'withdrawal' ? 'text-red-600' : 'text-green-600'
                            }`}>
                              {transaction.type === 'transfer_out' || transaction.type === 'withdrawal' ? '-' : '+'}
                              {formatCurrency(parseFloat(transaction.amount))}
                            </p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              transaction.status === 'completed' ? 'bg-green-100 text-green-800' :
                              transaction.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {transaction.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="space-y-6">
                  {/* Admin Revenue Panel */}
                  {isAdmin && (
                    <div className="border-4 border-green-500 rounded-xl p-6 bg-gradient-to-r from-green-50 to-emerald-50">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
                          <DollarSign className="w-6 h-6" />
                          💰 Admin Revenue Dashboard
                        </h3>
                        <button
                          onClick={() => {
                            setShowAdminPanel(!showAdminPanel);
                            if (!showAdminPanel) loadRevenueData();
                          }}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                        >
                          {showAdminPanel ? 'Hide' : 'Show Revenue'}
                        </button>
                      </div>
                      
                      {showAdminPanel && revenueData && (
                        <div className="space-y-4">
                          {/* Revenue Stats */}
                          <div className="grid md:grid-cols-3 gap-4">
                            <div className="bg-white p-4 rounded-lg shadow">
                              <p className="text-sm text-gray-600">Total Revenue</p>
                              <p className="text-2xl font-bold text-green-600">{formatCurrency(revenueData.total)}</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                              <p className="text-sm text-gray-600">Already Withdrawn</p>
                              <p className="text-2xl font-bold text-gray-600">{formatCurrency(revenueData.withdrawn)}</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow">
                              <p className="text-sm text-gray-600">Available to Withdraw</p>
                              <p className="text-2xl font-bold text-blue-600">{formatCurrency(revenueData.available)}</p>
                            </div>
                          </div>
                          
                          {/* Revenue by Type */}
                          <div className="bg-white p-4 rounded-lg shadow">
                            <h4 className="font-bold text-gray-800 mb-2">Revenue Breakdown</h4>
                            {revenueData.byType.map((item, idx) => (
                              <div key={idx} className="flex justify-between py-2 border-b">
                                <span className="capitalize">{item.fee_type}</span>
                                <span className="font-semibold">{formatCurrency(parseFloat(item.revenue))} ({item.count} transactions)</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Withdraw Revenue Form */}
                          {revenueData.available > 0 && (
                            <form onSubmit={handleAdminWithdraw} className="bg-blue-50 p-4 rounded-lg border-2 border-blue-300">
                              <h4 className="font-bold text-blue-900 mb-3">💳 Withdraw Your Revenue</h4>
                              <p className="text-sm text-blue-700 mb-3">Money will be sent to your bank account linked to Stripe in 1-2 business days</p>
                              
                              <div className="space-y-3">
                                <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-1">Amount to Withdraw</label>
                                  <div className="relative">
                                    <DollarSign className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
                                    <input
                                      type="number"
                                      value={adminWithdrawAmount}
                                      onChange={(e) => setAdminWithdrawAmount(e.target.value)}
                                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                                      placeholder="100.00"
                                      step="0.01"
                                      min="1"
                                      max={revenueData.available}
                                      required
                                    />
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">Available: {formatCurrency(revenueData.available)}</p>
                                </div>
                                
                                <button
                                  type="submit"
                                  disabled={loading || !adminWithdrawAmount}
                                  className="w-full py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 disabled:bg-gray-400"
                                >
                                  {loading ? 'Processing Payout...' : `💸 Withdraw ${adminWithdrawAmount ? formatCurrency(parseFloat(adminWithdrawAmount)) : '$0.00'} to Bank`}
                                </button>
                              </div>
                            </form>
                          )}
                          
                          {/* Recent Revenue */}
                          <div className="bg-white p-4 rounded-lg shadow max-h-64 overflow-y-auto">
                            <h4 className="font-bold text-gray-800 mb-2">Recent Revenue Transactions</h4>
                            <div className="space-y-2">
                              {revenueData.recent.slice(0, 10).map((item, idx) => (
                                <div key={idx} className="flex justify-between text-sm py-2 border-b">
                                  <div>
                                    <p className="font-semibold">{formatCurrency(parseFloat(item.amount))}</p>
                                    <p className="text-xs text-gray-500">{item.email} - {item.fee_type}</p>
                                  </div>
                                  <p className="text-xs text-gray-400">{new Date(item.created_at).toLocaleDateString()}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                          
                          <button
                            onClick={loadRevenueData}
                            className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
                          >
                            🔄 Refresh Data
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 mb-4">Account Settings</h3>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800">Name</p>
                          <p className="text-sm text-gray-600">{user?.firstName} {user?.lastName}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800">Email</p>
                          <p className="text-sm text-gray-600">{user?.email}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800 flex items-center gap-2">
                            <Shield className="w-5 h-5 text-blue-600" />
                            Two-Factor Authentication
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {user?.twoFactorEnabled ? '✓ Enabled' : 'Not enabled'}
                          </p>
                        </div>
                        {!user?.twoFactorEnabled ? (
                          <button
                            onClick={() => setShow2FATutorial(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                          >
                            Enable
                          </button>
                        ) : (
                          <button
                            onClick={handleDisable2FA}
                            disabled={loading}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                          >
                            {loading ? 'Disabling...' : 'Disable'}
                          </button>
                        )}
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-gray-800 flex items-center gap-2">
                            <Shield className="w-5 h-5 text-green-600" />
                            Stripe Verification
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {stripeVerified ? '✓ Verified' : 'Not verified'}
                          </p>
                        </div>
                        {!stripeVerified && (
                          <button
                            onClick={handleConnectStripe}
                            disabled={loading}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold"
                          >
                            {loading ? 'Connecting...' : 'Verify Now'}
                          </button>
                        )}
                      </div>

                      {/* Legal Disclaimer Section */}
                      <div className="p-4 bg-orange-50 border-2 border-orange-300 rounded-lg">
                        <div>
                          <p className="font-semibold text-orange-900 flex items-center gap-2 mb-2">
                            <AlertCircle className="w-5 h-5 text-orange-600" />
                            Legal Notice - Beta Software
                          </p>
                          <div className="text-xs text-orange-800 space-y-1">
                            <p>⚠️ <strong>This is experimental software in testing phase</strong></p>
                            <p>• Not for commercial use or large amounts</p>
                            <p>• Not FDIC insured - funds not federally protected</p>
                            <p>• Not a licensed money transmitter</p>
                            <p>• Use at your own risk - beta testing only</p>
                            <p>• Upgrading to FDIC-insured accounts via Stripe Treasury soon</p>
                            <p className="mt-2 font-semibold">Recommended limit: $100 maximum balance</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 2FA Tutorial Modal */}
        <TwoFATutorial
          isOpen={show2FATutorial}
          onClose={() => setShow2FATutorial(false)}
          onEnable={handleEnable2FA}
        />

        {/* AI Support Chat Modal */}
        {showSupportChat && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl h-[600px] flex flex-col">
              {/* Chat Header */}
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-t-2xl flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <div className="bg-white rounded-full p-2">
                    <Bot className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">P.A.Y.S. Support</h3>
                    <p className="text-xs text-blue-100">AI-Powered Help • Instant Answers</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSupportChat(false)}
                  className="text-white hover:text-blue-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Chat Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
                {supportMessages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white rounded-br-none'
                          : 'bg-white text-gray-800 border-2 border-gray-200 rounded-bl-none'
                      }`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <Bot className="w-4 h-4 text-blue-600" />
                          <span className="text-xs font-semibold text-blue-600">P.A.Y.S. Assistant</span>
                        </div>
                      )}
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}

                {supportLoading && (
                  <div className="flex justify-start">
                    <div className="bg-white border-2 border-gray-200 rounded-2xl rounded-bl-none px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Chat Input */}
              <div className="p-4 bg-white border-t-2 border-gray-200 rounded-b-2xl">
                <form onSubmit={handleSupportSubmit} className="flex gap-2">
                  <input
                    type="text"
                    value={supportInput}
                    onChange={(e) => setSupportInput(e.target.value)}
                    placeholder="Ask me anything about P.A.Y.S..."
                    className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                    disabled={supportLoading}
                  />
                  <button
                    type="submit"
                    disabled={!supportInput.trim() || supportLoading}
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-semibold"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </form>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Need human support? Email <a href="mailto:sniper44x@gmail.com" className="text-blue-600 hover:underline">sniper44x@gmail.com</a>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Floating Support Button */}
        {screen === 'dashboard' && (
          <button
            onClick={() => setShowSupportChat(true)}
            className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 transition-all duration-300 z-40 group"
          >
            <MessageCircle className="w-6 h-6" />
            <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-semibold whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
              Need Help? 💬
            </span>
          </button>
        )}
      </div>
    );
  }

  return null;
}
